/** The app's store listings — one place, for the UI and the SEO structured data. */
export const APP_STORE_URL =
  'https://apps.apple.com/us/app/flashka-ai-flashcards-maker/id6748599950';
export const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=ai.flashka.android.dev&hl=en';

export type AppStore = 'ios' | 'android';

/**
 * Where this device installs Flashka from: one store, `both` for a phone or
 * tablet we cannot place (offer the two), or `null` for a computer — which
 * installs neither, and keeps Get Started and the QR codes instead.
 */
export type StoreDevice = AppStore | 'both' | null;

type NavigatorLike = {
  userAgent: string;
  maxTouchPoints?: number;
  userAgentData?: { platform?: string; mobile?: boolean };
};

/**
 * The device's store, from its user agent:
 * - iPhone, iPod, iPad → App Store. iPadOS Safari asks for the desktop site
 *   and says "Macintosh", so a "Mac" with a touch screen is an iPad: no Mac
 *   has one (a Mac reports 0 touch points).
 * - Android (or a browser that reports it through client hints) → Google Play.
 * - Other phones and tablets (a Kindle, KaiOS — both say "Android" but use
 *   other stores — HarmonyOS without the Android token) → both, and the
 *   visitor picks.
 * - Everything else — Windows, macOS, Linux, ChromeOS, touch laptops too —
 *   is a computer.
 */
export function detectStoreDevice(nav: NavigatorLike | undefined): StoreDevice {
  if (!nav) return null;
  const ua = nav.userAgent || '';
  if (/iPhone|iPod|iPad/i.test(ua)) return 'ios';
  if (/Macintosh/i.test(ua) && (nav.maxTouchPoints ?? 0) > 1) return 'ios';
  // Kindle Fire and KaiOS say "Android" but install from other stores.
  if (/Silk|Kindle|KAIOS/i.test(ua)) return 'both';
  if (/Android/i.test(ua) || nav.userAgentData?.platform === 'Android') return 'android';
  if (/Mobile|Tablet|HarmonyOS|OpenHarmony|Windows Phone/i.test(ua)) return 'both';
  if (nav.userAgentData?.mobile) return 'both';
  return null;
}

/** A store's listing, Google Play's in the page's language. */
export function storeUrl(store: AppStore, lang?: string): string {
  if (store === 'ios') return APP_STORE_URL;
  if (!lang) return PLAY_STORE_URL;
  const url = new URL(PLAY_STORE_URL);
  url.searchParams.set('hl', lang);
  return url.toString();
}
